import type { CadDocument } from '../model/document';
import { entityBox, type BlockDef } from '../model/entity';
import { boxExpand, boxUnion, type Box } from '../model/geom';
import { Scene } from '../scene';
import { Viewport } from '../render/viewport';
import { renderScene, type RenderTheme } from '../render/renderer';
import {
  buildImagePdf,
  fitScale,
  pageSizeMm,
  pagePixels,
  type Orientation,
  type PaperSize,
} from './pdfExport';

/**
 * 인쇄 출력 (작업지시서 P3-11 / P3-12)
 *
 * 도면을 용지에 축척대로 배치하고 제목란·워터마크·QR 을 얹어
 * PDF 또는 PNG 로 낸다. 브라우저 전용 (캔버스가 필요하다).
 *
 * ※ 한때 유출 추적용으로 워터마크에 열람자 이메일을 반복 각인했으나,
 *   도면 자체를 알아보기 힘들 만큼 화면이 흐려진다는 실사용 피드백으로 뺐다.
 */

/**
 * 인쇄용은 흰 배경 + 검은 선. 화면용 어두운 테마를 그대로 쓰면 토너를 다 먹는다.
 *
 * ★ `entityColor` 로 도면 선을 전부 검정으로 강제한다.
 *   표준 레이어 색은 어두운 화면을 전제로 고른 밝은 파스텔(벽체 #E8E8E8,
 *   치수 #D9D9D9 …)이라 흰 종이에 그대로 찍으면 **아무것도 안 보인다.**
 *   실제 CAD 도 플롯 스타일로 화면색을 검은 펜에 매핑해 출력한다.
 */
export const PRINT_THEME: RenderTheme = {
  background: '#FFFFFF',
  grid: '#FFFFFF', // 격자는 인쇄하지 않는다
  gridMajor: '#FFFFFF',
  cursor: '#FFFFFF',
  selection: '#000000',
  selectionFill: 'transparent',
  snapMarker: '#FFFFFF',
  preview: '#FFFFFF',
  windowBox: '#FFFFFF',
  crossingBox: '#FFFFFF',
  text: '#000000',
  entityColor: '#000000',
};

export interface PrintOptions {
  paper: PaperSize;
  orientation: Orientation;
  /** 축척 분모. 미지정이면 용지에 맞춰 표준 축척 중에서 자동 선택 */
  scaleDenom?: number;
  dpi?: number;
  marginMm?: number;
  /** 제목란 */
  title: string;
  subtitle?: string;
  versionLabel?: string;
  /** 승인 전 도면임을 알리는 대각선 워터마크 */
  draftWatermark?: boolean;
  /** 현장에서 찍으면 최신 승인본이 열리는 QR (data URL 또는 이미지 엘리먼트) */
  qrImage?: CanvasImageSource | null;
  qrCaption?: string;
}

export interface PrintResult {
  blob: Blob;
  /** 실제로 사용된 축척 분모 (자동 선택 결과 확인용) */
  scaleDenom: number;
  widthPx: number;
  heightPx: number;
}

/* -------------------------------------------------------------------------- *
 * 공통: 용지 캔버스 만들기
 * -------------------------------------------------------------------------- */

function documentExtent(doc: CadDocument, blocks: Map<string, BlockDef>): Box {
  if (doc.entities.length === 0) return { minX: 0, minY: 0, maxX: 1_000_000, maxY: 1_000_000 };
  let box: Box | null = null;
  for (const e of doc.entities) {
    const b = entityBox(e, blocks);
    box = box ? boxUnion(box, b) : b;
  }
  return box ?? { minX: 0, minY: 0, maxX: 1_000_000, maxY: 1_000_000 };
}

/**
 * 도면을 용지 캔버스에 축척대로 그린다.
 * 반환된 캔버스는 PDF(JPEG)와 PNG 양쪽에서 재사용된다.
 */
export function renderToPaper(
  doc: CadDocument,
  opt: PrintOptions
): { canvas: HTMLCanvasElement; scaleDenom: number } {
  const dpi = opt.dpi ?? 300;
  const margin = opt.marginMm ?? 15;
  const spec = { paper: opt.paper, orientation: opt.orientation, dpi };

  const px = pagePixels(spec);
  const pageMm = pageSizeMm(spec);

  const canvas = document.createElement('canvas');
  canvas.width = px.w;
  canvas.height = px.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('캔버스를 만들 수 없습니다.');

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, px.w, px.h);

  // 도면 범위와 축척
  const scene = new Scene(doc);
  const ext = boxExpand(documentExtent(doc, scene.blocks), 0);
  const drawW = Math.max(1, ext.maxX - ext.minX);
  const drawH = Math.max(1, ext.maxY - ext.minY);

  const scaleDenom = opt.scaleDenom ?? fitScale(drawW, drawH, spec, margin);

  // 축척 → 화면 배율.
  //   도면 µm → 종이 mm : 1/(1000 · 분모)
  //   종이 mm → 픽셀    : dpi/25.4
  const pxPerUm = (1 / (1000 * scaleDenom)) * (dpi / 25.4);

  const vp = new Viewport();
  vp.resize(px.w, px.h);
  vp.scale = pxPerUm;
  vp.center = {
    x: Math.round((ext.minX + ext.maxX) / 2),
    y: Math.round((ext.minY + ext.maxY) / 2),
  };

  // 제목란 높이만큼 도면을 위로 올린다
  const titleBandPx = Math.round((28 / 25.4) * dpi);
  vp.center = { ...vp.center, y: vp.center.y - Math.round(titleBandPx / 2 / pxPerUm) };

  // 선 두께는 종이 기준(1/100mm)으로 찍는다 — 축척에 따라 실낱같이 얇아지면 안 된다
  renderScene(ctx, scene, vp, new Set(), { ...PRINT_THEME, lineWeightPxPerMm: dpi / 25.4 });

  drawTitleBlock(ctx, px, pageMm, dpi, scaleDenom, opt);
  if (opt.draftWatermark) drawDraftWatermark(ctx, px, dpi);

  return { canvas, scaleDenom };
}

/* -------------------------------------------------------------------------- *
 * 제목란 · 워터마크
 * -------------------------------------------------------------------------- */

function drawTitleBlock(
  ctx: CanvasRenderingContext2D,
  px: { w: number; h: number },
  pageMm: { w: number; h: number },
  dpi: number,
  scaleDenom: number,
  opt: PrintOptions
): void {
  const mm = (v: number) => (v / 25.4) * dpi;
  const bandH = mm(28);
  const pad = mm(5);
  const top = px.h - bandH - mm(8);

  ctx.save();
  ctx.strokeStyle = '#000000';
  ctx.fillStyle = '#000000';
  ctx.lineWidth = Math.max(1, mm(0.3));

  // 테두리
  ctx.strokeRect(mm(8), mm(8), px.w - mm(16), px.h - mm(16));
  // 제목란
  ctx.strokeRect(mm(8), top, px.w - mm(16), bandH);

  const baseFont = mm(4);
  ctx.textBaseline = 'top';

  ctx.font = `700 ${mm(5.5)}px 'Noto Sans KR', sans-serif`;
  ctx.fillText(opt.title, mm(8) + pad, top + pad);

  ctx.font = `${baseFont}px 'Noto Sans KR', sans-serif`;
  let y = top + pad + mm(8);
  if (opt.subtitle) {
    ctx.fillText(opt.subtitle, mm(8) + pad, y);
    y += mm(5.5);
  }
  if (opt.versionLabel) {
    ctx.fillText(opt.versionLabel, mm(8) + pad, y);
  }

  // 오른쪽: 축척 · 용지 · 출력일시
  ctx.textAlign = 'right';
  const rx = px.w - mm(8) - pad;
  ctx.font = `700 ${mm(5)}px 'Noto Sans KR', sans-serif`;
  ctx.fillText(`축척 1:${scaleDenom}`, rx, top + pad);
  ctx.font = `${baseFont}px 'Noto Sans KR', sans-serif`;
  ctx.fillText(
    `${opt.paper} ${opt.orientation === 'landscape' ? '가로' : '세로'} · ${pageMm.w}×${pageMm.h}mm`,
    rx,
    top + pad + mm(7)
  );
  ctx.fillText(new Date().toLocaleString('ko-KR'), rx, top + pad + mm(13));

  // QR — 현장에서 찍으면 항상 최신 승인본이 열린다 (v2 §5.1-⑤)
  if (opt.qrImage) {
    const qr = mm(20);
    const qx = px.w - mm(8) - qr - pad;
    const qy = top + bandH - qr - mm(2);
    ctx.drawImage(opt.qrImage, qx, qy, qr, qr);
    if (opt.qrCaption) {
      ctx.textAlign = 'center';
      ctx.font = `${mm(2.6)}px 'Noto Sans KR', sans-serif`;
      ctx.fillText(opt.qrCaption, qx + qr / 2, qy + qr + mm(0.5));
    }
  }

  ctx.restore();
}

function drawDraftWatermark(
  ctx: CanvasRenderingContext2D,
  px: { w: number; h: number },
  dpi: number
): void {
  ctx.save();
  ctx.translate(px.w / 2, px.h / 2);
  ctx.rotate(-Math.atan2(px.h, px.w));
  ctx.globalAlpha = 0.1;
  ctx.fillStyle = '#000000';
  ctx.font = `800 ${(60 / 25.4) * dpi}px 'Noto Sans KR', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('승인 전 · 시공 금지', 0, 0);
  ctx.restore();
}

/* -------------------------------------------------------------------------- *
 * 출력
 * -------------------------------------------------------------------------- */

export async function exportPdf(doc: CadDocument, opt: PrintOptions): Promise<PrintResult> {
  const { canvas, scaleDenom } = renderToPaper(doc, opt);

  // JPEG 로 뽑아 그대로 PDF /DCTDecode 에 넣는다 — 압축 구현이 불필요하다.
  const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
  const jpeg = new Uint8Array(await jpegBlob.arrayBuffer());

  const pdf = buildImagePdf(
    {
      jpeg,
      widthPx: canvas.width,
      heightPx: canvas.height,
      paper: opt.paper,
      orientation: opt.orientation,
    },
    {
      title: opt.title,
      subject: `${opt.subtitle ?? ''} ${opt.versionLabel ?? ''}`.trim(),
      author: 'StoreCAD',
    }
  );

  return {
    blob: new Blob([pdf as unknown as ArrayBuffer], { type: 'application/pdf' }),
    scaleDenom,
    widthPx: canvas.width,
    heightPx: canvas.height,
  };
}

export async function exportPng(doc: CadDocument, opt: PrintOptions): Promise<PrintResult> {
  const { canvas, scaleDenom } = renderToPaper(doc, opt);
  const blob = await canvasToBlob(canvas, 'image/png');
  return { blob, scaleDenom, widthPx: canvas.width, heightPx: canvas.height };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('이미지를 만들지 못했습니다.'))),
      type,
      quality
    );
  });
}

/** 브라우저에서 파일로 내려받기 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 즉시 해제하면 일부 브라우저에서 다운로드가 취소된다
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
